import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Close,
  MenuBook,
  School,
  TrendingUp,
  Score,
  Timer,
  CalendarToday,
  ExpandMore,
  ArrowCircleLeftOutlined,
  ArrowCircleRightOutlined,
  Person,
} from "@mui/icons-material";
import { axiosInstance } from "../../../services/api";
import SectionStatistics from "../core/SectionStatistics";
import { numberToGematria } from "../../../utils/text-hebrew.utils";

// Types
interface StudentDetails {
  id: number;
  name: string;
  email: string;
  role_name: string;
  statistics: {
    totalBooks: number;
    totalReadingSessions: number;
    totalStudySessions: number;
    averageReadingScore: number;
    averageStudyScore: number;
    lastActiveDate: string;
  };
  books: Array<{
    bookName: string;
    readingSessionsCount: number;
    studySessionsCount: number;
    averageReadingScore: number;
    averageStudyScore: number;
    lastReadDate: string;
  }>;
  detailedBooks: Array<{
    book_name_en: string;
    book_name_he: string;
    book_id: number;
    book_statistics: {
      total_reading: number;
      total_by_heart: number;
      total_score: number;
      sub_category_array: Array<{
        sub_category_name: string;
        sub_category_name_he: string;
        top_section_count: number;
        section_count: number;
        read_count: number;
        top_section_array: Array<{
          top_section_name: string | number;
          top_section_id: number;
          section_array: Array<{
            section_name: string | number;
            by_heart: {
              read_count: number;
              last_score: number;
              average_score: number;
              high_score: number;
            };
            in_writing: {
              read_count: number;
              last_score: number;
              average_score: number;
              high_score: number;
            };
          }>;
        }>;
      }>;
      lastScore: any;
    };
  }>;
}

interface StudentDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  studentId: number | null;
  studentName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`student-tabpanel-${index}`}
      aria-labelledby={`student-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StudentDetailsDrawer: React.FC<StudentDetailsDrawerProps> = ({
  open,
  onClose,
  studentId,
  studentName,
}) => {
  // console.log("StudentDetailsDrawer props:", { open, studentId, studentName }); // הסרתי כדי למנוע ספאם בקונסול
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [studentData, setStudentData] = useState<StudentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBookName, setSelectedBookName] = useState<string>("");
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [selectedLevelTwo, setSelectedLevelTwo] = useState<any | null>(null);
  const [selectedLevelThree, setSelectedLevelThree] = useState<any | null>(null);

  // טעינת נתוני התלמיד
  useEffect(() => {
    // console.log("Drawer useEffect triggered - open:", open, "studentId:", studentId); // הסרתי כדי למנוע ספאם בקונסול
    if (open && studentId) {
      loadStudentData();
    }
  }, [open, studentId]);

  const loadStudentData = async () => {
    if (!studentId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/admin/student-data/${studentId}`);
      setStudentData(response.data);
    } catch (error: any) {
      console.error("Error loading student data:", error);
      setError("שגיאה בטעינת נתוני התלמיד");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setTabValue(0);
    setStudentData(null);
    setError(null);
    setSelectedBookName("");
    setExpandedAccordion(false);
    setSelectedLevelTwo(null);
    setSelectedLevelThree(null);
    onClose();
  };

  const handleBookNameChange = (event: any) => {
    setSelectedBookName(event.target.value);
    setExpandedAccordion(false);
    setSelectedLevelTwo(null);
    setSelectedLevelThree(null);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Auto-select first book when student data is loaded
  useEffect(() => {
    if (studentData && !selectedBookName && studentData.detailedBooks.length > 0) {
      setSelectedBookName(studentData.detailedBooks[0].book_name_en);
    }
  }, [studentData, selectedBookName]);

  // Helper functions for different book types (copied from LevelOneAccordion)
  const displayNames: Record<string, string[]> = {
    'משנה': ['פרקים', 'משניות', 'משנה'],
    'סידור אשכנז': ['חלקים', 'נושאים', 'תת-נושאים'], // עדכון שמות לסידור
    'תורה': ['פרקים', 'פסוקים', 'פסוק'],
    'נביאים': ['פרקים', 'פסוקים', 'פסוק'],
    'כתובים': ['פרקים', 'פסוקים', 'פסוק'],
  };
  const defaultDisplayNames = ['חלקים', 'נושאים', 'קטע'];
  
  const getDisplayName = (bookName: string, levelIdx: number) => {
    return (displayNames[bookName]?.[levelIdx]) || defaultDisplayNames[levelIdx];
  };

  const getCountLabel = (bookName: string) => {
    const labelMap: Record<string, string> = {
      'משנה': 'manyMishnayot',
      'סידור אשכנז': 'manyReadingSections',
      'תורה': 'manyVerses',
      'נביאים': 'manyVerses',
      'כתובים': 'manyVerses',
    };
    return labelMap[bookName] || 'manyReadingSections';
  };

  const getPluralLabel = (bookName: string) => {
    const labelMap: Record<string, string> = {
      'משנה': 'mishnayot',
      'סידור אשכנז': 'readingSections',
      'תורה': 'verses',
      'נביאים': 'verses',
      'כתובים': 'verses',
    };
    return labelMap[bookName] || 'readingSections';
  };

  // Map specific book names to categories
  const getBookCategory = (bookNameHe: string): string => {
    const lowerBookName = bookNameHe.toLowerCase();
    
    // Check for Torah books (more flexible matching)
    const torahKeywords = ['בראשית', 'שמות', 'ויקרא', 'במדבר', 'דברים', 'תורה'];
    if (torahKeywords.some(keyword => lowerBookName.includes(keyword))) return 'תורה';
    
    // Check for Neviim books
    const neviimKeywords = ['יהושע', 'שופטים', 'שמואל', 'מלכים', 'ישעיהו', 'ירמיהו', 'יחזקאל', 'נביאים'];
    if (neviimKeywords.some(keyword => lowerBookName.includes(keyword))) return 'נביאים';
    
    // Check for Ketuvim books
    const ketuvimKeywords = ['תהילים', 'משלי', 'איוב', 'שיר השירים', 'רות', 'איכה', 'קהלת', 'אסתר', 'דניאל', 'עזרא', 'נחמיה', 'דברי הימים', 'כתובים'];
    if (ketuvimKeywords.some(keyword => lowerBookName.includes(keyword))) return 'כתובים';
    
    // Check for Mishna books
    if (lowerBookName.includes('משנה') || lowerBookName.includes('מסכת')) return 'משנה';
    
    // Check for Siddur
    if (lowerBookName.includes('סידור')) return 'סידור אשכנז';
    
    return 'משנה'; // default fallback
  };

  // Auto-select first level two and three when book changes or accordion opens
  useEffect(() => {
    if (studentData && selectedBookName) {
      const currentBook = studentData.detailedBooks.find(
        (book) => book.book_name_en === selectedBookName
      );
      if (currentBook && currentBook.book_statistics?.sub_category_array && currentBook.book_statistics.sub_category_array.length > 0) {
        const firstSubCategory = currentBook.book_statistics.sub_category_array[0];
        if (firstSubCategory.top_section_array && firstSubCategory.top_section_array.length > 0) {
          const firstTopSection = firstSubCategory.top_section_array[0];
          setSelectedLevelTwo(firstTopSection);
          if (firstTopSection.section_array && firstTopSection.section_array.length > 0) {
            setSelectedLevelThree(firstTopSection.section_array[0]);
          }
        }
      }
    }
  }, [studentData, selectedBookName]);

  // איפוס בחירה שלישית כאשר הבחירה השנייה משתנה (בדיוק כמו באזור האישי)
  useEffect(() => {
    if (selectedLevelTwo && selectedLevelTwo.section_array?.length > 0) {
      setSelectedLevelThree(selectedLevelTwo.section_array[0]);
    }
  }, [selectedLevelTwo]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "700px", md: "900px", lg: "1000px" },
          maxWidth: "95vw",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            פרטי תלמיד: {studentName || "טוען..."}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", overflowX: "hidden", width: "100%" }}>
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {studentData && !loading && !error && (
            <>
              {/* Tabs */}
              <Box 
                sx={{ 
                  borderBottom: 1, 
                  borderColor: "divider",
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 20,
                  mb: 0
                }}
              >
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label={t('overview')} />
                  <Tab label={t('booksLearned')} />
                </Tabs>
              </Box>

              {/* Tab 1: Overview */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ space: 3 }}>
                  {/* פרטי משתמש - עיצוב מחודש */}
                  <Box sx={{ mb: 3 }}>
                    <div style={{ 
                      backgroundColor: '#f9f7f4', 
                      border: '2px solid #5898ff',
                      borderRadius: '12px',
                      padding: 'clamp(16px, 4vw, 20px)',
                      marginBottom: '16px',
                      boxShadow: '0 2px 8px rgba(88, 152, 255, 0.15)'
                    }}>
                      {/* כותרת */}
                      <div style={{ 
                        textAlign: 'center', 
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#5898ff',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                        <Person sx={{ fontSize: 24, color: '#5898ff' }} />
                        פרטי המשתמש
                      </div>
                      
                      {/* פריסת הפרטים */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '12px'
                      }}>
                        {/* שם */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: 'clamp(12px, 3vw, 16px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(88, 152, 255, 0.2)',
                          gap: '12px'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#5898ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}>
                            שם
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: '500',
                              marginBottom: '2px'
                            }}>
                              שם:
                            </div>
                            <div style={{
                              fontSize: 'clamp(14px, 3.5vw, 16px)',
                              fontWeight: '600',
                              color: '#333',
                              wordBreak: 'break-word'
                            }}>
                              {studentData.name}
                            </div>
                          </div>
                        </div>

                        {/* אימייל */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: 'clamp(12px, 3vw, 16px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(76, 175, 80, 0.2)',
                          gap: '12px'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}>
                            @
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: '500',
                              marginBottom: '2px'
                            }}>
                              אימייל:
                            </div>
                            <div style={{
                              fontSize: 'clamp(14px, 3.5vw, 16px)',
                              fontWeight: '600',
                              color: '#333',
                              wordBreak: 'break-all',
                              direction: 'ltr',
                              textAlign: 'left'
                            }}>
                              {studentData.email}
                            </div>
                          </div>
                        </div>

                        {/* תפקיד */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: 'clamp(12px, 3vw, 16px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 193, 7, 0.2)',
                          gap: '12px'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#ffc107',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}>
                            ת
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: '500',
                              marginBottom: '2px'
                            }}>
                              תפקיד:
                            </div>
                            <div style={{
                              display: 'inline-block',
                              backgroundColor: '#ffc107',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '600'
                            }}>
                              {studentData.role_name}
                            </div>
                          </div>
                        </div>

                        {/* פעילות אחרונה */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: 'clamp(12px, 3vw, 16px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(156, 39, 176, 0.2)',
                          gap: '12px'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#9c27b0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}>

                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: '500',
                              marginBottom: '2px'
                            }}>
                              פעילות אחרונה:
                            </div>
                            <div style={{
                              fontSize: 'clamp(14px, 3.5vw, 16px)',
                              fontWeight: '600',
                              color: studentData.statistics.lastActiveDate ? '#333' : '#999',
                              fontStyle: !studentData.statistics.lastActiveDate ? 'italic' : 'normal'
                            }}>
                              {studentData.statistics.lastActiveDate || "לא זמין"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Box>

                  {/* סטטיסטיקות כלליות - עיצוב מחודש */}
                  <Box sx={{ mb: 3 }}>
                    <div style={{ 
                      backgroundColor: '#f5f3f0', 
                      border: '2px solid #9c27b0',
                      borderRadius: '12px',
                      padding: 'clamp(16px, 4vw, 20px)',
                      marginBottom: '16px',
                      boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)'
                    }}>
                      {/* כותרת */}
                      <div style={{ 
                        textAlign: 'center', 
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#9c27b0',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                        <TrendingUp sx={{ fontSize: 24, color: '#9c27b0' }} />
                        סטטיסטיקות כלליות
                      </div>
                      
                      {/* שורה ראשונה - 3 סטטיסטיקות */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        {/* ספרים נלמדו */}
                                                 <div style={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           backgroundColor: 'rgba(255, 255, 255, 0.8)',
                           padding: 'clamp(8px, 3vw, 12px)',
                           borderRadius: '8px',
                           border: '1px solid rgba(156, 39, 176, 0.2)'
                         }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#5898ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}>
                            ס
                          </div>
                          <div style={{
                            fontSize: 'clamp(20px, 5vw, 24px)',
                            fontWeight: 'bold',
                            color: '#5898ff',
                            lineHeight: 1
                          }}>
                            {studentData.statistics.totalBooks}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            ספרים נלמדו
                          </div>
                        </div>

                        {/* קריאות */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(76, 175, 80, 0.2)'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}>
                            ק
                          </div>
                          <div style={{
                            fontSize: 'clamp(20px, 5vw, 24px)',
                            fontWeight: 'bold',
                            color: '#4caf50',
                            lineHeight: 1
                          }}>
                            {studentData.statistics.totalReadingSessions}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            קריאות
                          </div>
                        </div>

                        {/* שינונים */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 193, 7, 0.2)'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#ffc107',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}>
                            ש
                          </div>
                          <div style={{
                            fontSize: 'clamp(20px, 5vw, 24px)',
                            fontWeight: 'bold',
                            color: '#ffc107',
                            lineHeight: 1
                          }}>
                            {studentData.statistics.totalStudySessions}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            שינונים
                          </div>
                        </div>
                      </div>

                      {/* שורה שנייה - 2 ממוצעים */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '12px'
                      }}>
                        {/* ממוצע קריאה */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(220, 53, 69, 0.2)'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#dc3545',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}>
                            מק
                          </div>
                          <div style={{
                            fontSize: 'clamp(20px, 5vw, 24px)',
                            fontWeight: 'bold',
                            color: '#dc3545',
                            lineHeight: 1
                          }}>
                            {studentData.statistics.averageReadingScore > 0 ? 
                              `${Math.round(studentData.statistics.averageReadingScore)}%` : '--'}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            ממוצע קריאה
                          </div>
                        </div>

                        {/* ממוצע שינון */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(111, 66, 193, 0.2)'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#6f42c1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}>
                            מש
                          </div>
                          <div style={{
                            fontSize: 'clamp(20px, 5vw, 24px)',
                            fontWeight: 'bold',
                            color: '#6f42c1',
                            lineHeight: 1
                          }}>
                            {studentData.statistics.averageStudyScore > 0 ? 
                              `${Math.round(studentData.statistics.averageStudyScore)}%` : '--'}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            ממוצע שינון
                          </div>
                        </div>
                      </div>
                    </div>
                  </Box>
                </Box>
              </TabPanel>

              {/* Tab 2: Books - Personal Area Style */}
              <TabPanel value={tabValue} index={1}>
                {studentData.detailedBooks.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <MenuBook sx={{ fontSize: 60, color: "#9e9e9e", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      עדיין לא נלמדו ספרים
                    </Typography>
                  </Box>
                ) : (
                  <Box className="my-reading-page student-area-view">
                    {/* Book Selector - Vertical dropdown instead of horizontal tabs */}
                    {studentData.detailedBooks.length > 1 && (
                      <Box 
                        sx={{ 
                          mb: 3,
                          mt: 0,
                          pt: 0,
                        }}
                      >
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>בחר ספר</InputLabel>
                          <Select
                            value={selectedBookName || studentData.detailedBooks[0]?.book_name_en}
                            onChange={handleBookNameChange}
                            label="בחר ספר"
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              PaperProps: {
                                sx: {
                                  marginTop: '4px',
                                  maxHeight: '300px'
                                }
                              }
                            }}
                          >
                            {studentData.detailedBooks.map((book) => (
                              <MenuItem key={book.book_name_en} value={book.book_name_en}>
                                {book.book_name_he}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                                                        {(() => {
                      const currentBookName = selectedBookName || studentData.detailedBooks[0]?.book_name_en;
                      const selectedBook = studentData.detailedBooks.find(
                        (book) => book.book_name_en === currentBookName
                      );
                      
                      if (!selectedBook) return null;

                      // Get book category for correct terminology
                      const bookCategory = getBookCategory(selectedBook.book_name_he);

                      return (
                        <Box sx={{ width: '100%', maxWidth: '100%' }}>
                          {/* Summary Statistics */}
                          <div className="summary-table" style={{ 
                            backgroundColor: '#f5f3f0', 
                            border: '2px solid #9c27b0',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '24px',
                            width: '80%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            display: 'grid',
                            gridTemplateColumns: '1fr auto 1fr auto',
                            gap: '16px',
                            alignItems: 'center'
                          }}>
                            <div className="grid-item" style={{ 
                              gridColumn: '1 / -1', 
                              textAlign: 'center', 
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#9c27b0',
                              marginBottom: '16px'
                            }}>
                              {selectedBook.book_name_he} - סיכום
                            </div>
                            
                            <div className="grid-item" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              fontSize: '16px',
                              fontWeight: '500'
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#9c27b0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}>
                                ש
                              </div>
                              סה"כ שינונים
                            </div>
                            <div className="grid-item summary-value" style={{
                              fontSize: '20px',
                              fontWeight: 'bold',
                              color: '#9c27b0'
                            }}>
                              {selectedBook.book_statistics.total_by_heart || 0}
                            </div>
                            
                            <div className="grid-item" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              fontSize: '16px',
                              fontWeight: '500'
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#4caf50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}>
                                ק
                              </div>
                              סה"כ קריאות
                            </div>
                            <div className="grid-item summary-value" style={{
                              fontSize: '20px',
                              fontWeight: 'bold',
                              color: '#4caf50'
                            }}>
                              {selectedBook.book_statistics.total_reading || 0}
                            </div>
                          </div>

                          {/* Masechet Accordions */}
                          <div className="levelOne-accordions" style={{ width: '100%', overflow: 'hidden', maxWidth: '100%', boxSizing: 'border-box' }}>
                          {selectedBook.book_statistics.sub_category_array && selectedBook.book_statistics.sub_category_array.length > 0 ? (
                            selectedBook.book_statistics.sub_category_array.map((subCategory, subIndex) => {
                            const progress = (subCategory.read_count / subCategory.section_count) * 100;
                            const accordionId = `panel${subIndex}`;
                            const isExpanded = expandedAccordion === accordionId || selectedBook.book_statistics.sub_category_array.length === 1;

                            return (
                              <Accordion
                                key={subIndex}
                                expanded={isExpanded}
                                onChange={handleAccordionChange(accordionId)}
                                  slotProps={{ transition: { timeout: { enter: 600, exit: 800 } } }}
                                  sx={{ width: '100%', maxWidth: '100%' }}
                              >
                                <AccordionSummary
                                  expandIcon={<ArrowCircleLeftOutlined />}
                                  >
                                    <div className="headers">
                                      <h3 style={{ color: 'primary' }}>{subCategory.sub_category_name_he}</h3>
                                      <h5>{`${subCategory.top_section_count} ${t('chapters')} | ${
                                        subCategory.section_count
                                      }  ${t(getPluralLabel(bookCategory))}`}</h5>
                                    </div>
                                    <div className="progress">
                                      <label style={{ width: 'max-content' }}>{`${t(getCountLabel(bookCategory))}: ${subCategory.section_count} / ${
                                        subCategory.read_count
                                      }`}</label>
                                      <div style={{ transform: 'scaleX(-1)' }}>
                                        <LinearProgress variant="determinate" value={progress} />
                                      </div>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '0 !important', overflow: 'hidden' }}>
                                  <div className="container" style={{ width: '100%', overflow: 'auto', maxHeight: '340px', boxSizing: 'border-box' }}>
                                    {subCategory.top_section_array?.length > 0 && (
                                      <div className="column">
                                        <h5 style={{ paddingInline: '6px' }}>{getDisplayName(bookCategory, 0)}</h5>
                                        <List>
                                          {subCategory.top_section_array.map((topSection, index) => (
                                            <ListItemButton
                                              key={topSection.top_section_id}
                                              selected={selectedLevelTwo?.top_section_id === topSection.top_section_id}
                                              onClick={() => {
                                                setSelectedLevelTwo(topSection);
                                                setSelectedLevelThree(topSection.section_array?.[0] || null);
                                              }}
                                            >
                                              <ListItemText
                                                primary={<p>{numberToGematria(topSection.top_section_name ?? index + 1)}</p>}
                                              />
                                            </ListItemButton>
                                          ))}
                                        </List>
                                      </div>
                                    )}
                                    
                                    {selectedLevelTwo && Array.isArray(selectedLevelTwo.section_array) && selectedLevelTwo.section_array.length > 0 && (
                                      <div className="column">
                                        <h5 style={{ paddingInline: '6px' }}>{getDisplayName(bookCategory, 1)}</h5>
                                        <List>
                                          {selectedLevelTwo.section_array.map((levelThree: any) => (
                                            <ListItemButton
                                              key={levelThree.section_name}
                                              selected={selectedLevelThree?.section_name === levelThree.section_name}
                                              onClick={() => setSelectedLevelThree(levelThree)}
                                            >
                                              <ListItemText primary={<p>{numberToGematria(levelThree.section_name)}</p>} />
                                            </ListItemButton>
                                          ))}
                                        </List>
                                      </div>
                                    )}
                                    
                                    <div className="column">
                                      {selectedLevelThree && (
                                        <Box>
                                          <div className="details">
                                            <div className="head-line">
                                              <h3>
                                                {`${getDisplayName(bookCategory, 2)} ${numberToGematria(selectedLevelThree?.section_name)}`}
                                              </h3>
                                              <h4>
                                                {`${subCategory.sub_category_name_he} | ${(() => {
                                                  const sectionName =
                                                    selectedLevelTwo?.top_section_name ??
                                                    selectedLevelTwo?.top_section_name;
                                                  if (sectionName === undefined || sectionName === null) {
                                                    return '';
                                                  }
                                                  const sectionNumber = Number(sectionName);
                                                  if (isNaN(sectionNumber)) {
                                                    return sectionName;
                                                  }
                                                  return (
                                                    `${t('chapter')} ` + numberToGematria(sectionNumber)
                                                  );
                                                })()}`}
                                              </h4>
                                            </div>
                                            <SectionStatistics
                                              readingStats={selectedLevelThree.in_writing}
                                              byHeartStats={selectedLevelThree.by_heart}
                                              showAverageScore={true}
                                            />
                                          </div>
                                        </Box>
                                      )}
                                    </div>
                                  </div>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })) : (
                            <Box sx={{ p: 3, textAlign: "center" }}>
                              <Typography variant="body1" color="text.secondary">
                                אין נתונים זמינים עבור ספר זה
                              </Typography>
                            </Box>
                          )}
                          </div>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </TabPanel>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default StudentDetailsDrawer; 